import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { OtpInput } from '@/components/OtpInput'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_CONFIRM_COPY } from '@/copy/registry'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/**
 * `a-confirm` — process/09-SCREEN-SPECS/a-confirm.md. Two operations, both behind a control:
 * `identityConfirmAddress` (Confirm) and `identityResendConfirmation` (Resend it). Family rules
 * A1–A10 (process/09-SCREEN-SPECS/_family-auth.md) bind here in full; this component owns only
 * what the spec's own table adds beyond them.
 *
 * `email` and `invitation` are carried on the auth state machine's own `a-confirm` state
 * (`src/layers/auth/state.ts`, LLD-SPA-060) — this screen has no field for either, matching
 * Prototype_v2.html, which shows the email as read-only lead text.
 */
export function AConfirm({
  email,
  invitation,
  onConfirmed,
}: {
  email: string
  invitation?: string
  onConfirmed: () => void
}) {
  const [code, setCode] = useState('')

  const confirm = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/confirm', { body: { email, code, invitation } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: onConfirmed,
  })

  const resend = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.POST('/auth/confirm/resend', { body: { email } })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const confirmProblem = confirm.error instanceof ApiProblemError ? confirm.error.problem : undefined
  const resendProblem = resend.error instanceof ApiProblemError ? resend.error.problem : undefined

  return (
    <AuthCard unitId={unitId('a-confirm')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_CONFIRM_COPY.heading}</h3>
      <AuthCardLead>{A_CONFIRM_COPY.leadTemplate(email)}</AuthCardLead>

      <FieldStack>
        <Field label={A_CONFIRM_COPY.codeFieldLabel}>
          <OtpInput value={code} onChange={setCode} />
        </Field>
      </FieldStack>

      {confirmProblem && <ConfirmRefusal problem={confirmProblem} />}

      <Button
        data-act={actionId('session.confirm')}
        full
        disabled={confirm.isPending || code.length < 6}
        onClick={() => confirm.mutate()}
      >
        {confirm.isPending ? 'Confirming…' : A_CONFIRM_COPY.confirmButton}
      </Button>

      <Note>{A_CONFIRM_COPY.noSignInNote}</Note>

      <Note>
        {A_CONFIRM_COPY.resendPromptPrefix}
        <a
          href="#"
          data-act={actionId('session.resendConfirmation')}
          onClick={(e) => {
            e.preventDefault()
            resend.mutate()
          }}
          className="text-link"
        >
          {A_CONFIRM_COPY.resendLinkLabel}
        </a>
        {A_CONFIRM_COPY.resendPromptSuffix}
      </Note>

      {resend.isSuccess && resend.data && <Note>{resend.data.display}</Note>}
      {resendProblem && <ResendRefusal problem={resendProblem} />}
    </AuthCard>
  )
}

function ConfirmRefusal({ problem }: { problem: Problem }) {
  switch (problem.refusal_kind) {
    case 'CREDENTIAL_REJECTED':
    case 'NOT_FOUND':
    case 'INVITATION_ADDRESS_MISMATCH':
      // A3: non-enumerating — the same copy whichever of the three caused it.
      return <FormError>{A_CONFIRM_COPY.codeRejectedCopy}</FormError>
    case 'VALIDATION_FAILED':
      return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
    case 'RATE_LIMITED':
      return <RateLimitedNotice problem={problem} />
    default:
      return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
  }
}

function ResendRefusal({ problem }: { problem: Problem }) {
  if (problem.refusal_kind === 'VALIDATION_FAILED') {
    return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
  }
  if (problem.refusal_kind === 'RATE_LIMITED') return <RateLimitedNotice problem={problem} />
  return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
}
